/**
 * Bundle Cart Behavior
 * - Intercepts ATC form submit (capture phase) to prevent cart page navigation
 * - Adds to cart via fetch, shows "Added!", updates cart badge
 * - Hides "You may also like", shows post-ATC section, scrolls to it
 * - Dispatches cart:updated + bundle:cart-updated for bundle components
 *
 * S30 additions (loaded on every page now, not just product):
 * - B3G1F count tracker: detects 2→3 transition, dispatches `punch-card:unlocked`
 * - PDP ATC at count=3: coral takeover (.atc--unlock) + "Add free shirt to cart" label
 * - Confetti listener: one burst per session (sessionStorage flag), respects reduced-motion
 */
(function() {
  'use strict';
  var EXCLUDED_TYPES = ['accessories','hats','mugs','artwork','art print','gift card'];

  // --- ATC form interception (capture phase — runs before theme handler) ---
  document.addEventListener('submit', function(e) {
    var form = e.target;
    // Only intercept product add-to-cart forms
    if (!form || (form.getAttribute('is') !== 'product-form' && !(form.action || '').includes('/cart/add'))) return;
    // Only on product pages where post-ATC section exists
    if (!document.getElementById('bundle-post-atc')) return;

    e.preventDefault();
    e.stopPropagation();

    var btn = form.querySelector('.product-form__submit') || form.querySelector('[name="add"]');
    var textSpan = btn && btn.querySelector('.btn-text > span');
    var originalText = textSpan ? textSpan.textContent : '';

    // Show working state
    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
    }

    // Build form data — enable the hidden id input first
    var hiddenId = form.querySelector('input[name="id"][disabled]');
    if (hiddenId) hiddenId.disabled = false;
    var formData = new FormData(form);
    if (hiddenId) hiddenId.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function(res) {
      if (!res.ok) throw new Error('ATC failed: ' + res.status);
      return res.json();
    })
    .then(function(item) {
      // Show "Added!" on button. After restore, re-apply any post-ATC override
      // (count=3 \u2192 "Add free shirt to cart"), since trackBundleCount may have
      // skipped the label change while aria-busy was true.
      if (textSpan) {
        textSpan.textContent = '\u2713 Added!';
        setTimeout(function() {
          textSpan.textContent = btn.dataset.thtOriginalLabel || originalText;
          if (typeof trackBundleCount === 'function') trackBundleCount();
        }, 1500);
      }
      if (btn) {
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
      }

      // Show confirmation banner in post-ATC stepper
      var confirmBanner = document.querySelector('.patc-confirm-banner');
      var confirmText = document.querySelector('.patc-confirm-text');
      if (confirmBanner && confirmText) {
        var productTitle = item.product_title || 'Item';
        confirmText.textContent = productTitle + ' added to cart';
        confirmBanner.style.display = 'flex';
      }

      // Update cart badge
      updateCartBadge();

      // Hide "You may also like", show post-ATC section
      toggleSections(true);

      // Scroll to post-ATC section — offset for sticky header + breathing room
      var postAtc = document.getElementById('bundle-post-atc');
      if (postAtc) {
        requestAnimationFrame(function() {
          var header = document.querySelector('.shopify-section-header') || document.querySelector('header');
          var headerH = header ? header.getBoundingClientRect().height : 0;
          var targetY = postAtc.getBoundingClientRect().top + window.pageYOffset - headerH - 20;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        });
      }

      // Dispatch events for other bundle components to refresh
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('bundle:cart-updated'));
      // Also fire the theme's event so other theme components update
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

      // Refresh the cart drawer's section HTML directly.
      // The theme's cart-items uses pubsub (not our DOM events) with section bundling.
      // We bypass that and re-fetch the section ourselves, replacing MiniCart innerHTML.
      refreshCartDrawer();

      // Force all steppers to re-render after section is visible
      // Belt-and-suspenders: the custom element's deferred connectedCallback
      // handles initial sync, but this ensures re-render after ATC
      setTimeout(function() {
        document.querySelectorAll('bundle-tier-display').forEach(function(el) {
          if (typeof el.load === 'function') el.load();
        });
        // Override post-ATC nudge text after stepper renders
        // The stepper's render() sets tier-based nudge text; we override
        // the post-ATC instance only to show discovery messaging
        overridePostAtcNudge();
      }, 100);
    })
    .catch(function(err) {
      console.error('[ATC] Failed:', err);
      if (textSpan) textSpan.textContent = originalText;
      if (btn) {
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
      }
      // Fallback: show error briefly on button
      if (textSpan) {
        textSpan.textContent = 'Error \u2014 try again';
        setTimeout(function() {
          textSpan.textContent = btn.dataset.thtOriginalLabel || originalText;
          if (typeof trackBundleCount === 'function') trackBundleCount();
        }, 2000);
      }
    });
  }, true); // capture phase — fires before theme's handler

  // --- Cart drawer refresh via Section Rendering API ---
  function refreshCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var sectionId = drawer.getAttribute('data-section-id');
    if (!sectionId) return;
    var miniCartId = 'MiniCart-' + sectionId;
    var miniCart = document.getElementById(miniCartId);
    if (!miniCart) return;

    var rootUrl = (window.theme && theme.routes && theme.routes.root_url) || '/';
    fetch(rootUrl + '?section_id=' + sectionId)
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newContent = doc.getElementById(miniCartId);
        if (newContent) {
          miniCart.innerHTML = newContent.innerHTML;
        }
      })
      .catch(function() {});
  }

  // --- Cart badge updater ---
  function updateCartBadge() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Update all cart count elements (header, drawer, mobile dock)
        document.querySelectorAll('.header__cart-count, [data-cart-count], cart-count, .cart-count-bubble span[aria-hidden]').forEach(function(el) {
          el.textContent = cart.item_count;
          // Remove hidden attribute on 0→1 transition
          if (cart.item_count > 0) el.removeAttribute('hidden');
          else el.setAttribute('hidden', '');
        });
        var bubble = document.querySelector('.cart-count-bubble');
        if (bubble) {
          var span = bubble.querySelector('span[aria-hidden="true"]');
          if (span) span.textContent = cart.item_count;
          bubble.classList.toggle('hidden', cart.item_count === 0);
        }
      })
      .catch(function() {});
  }

  // --- Section show/hide ---
  function toggleSections(showPostATC) {
    // Hide "You may also like" / product-recommendations
    document.querySelectorAll('h2').forEach(function(h2) {
      var text = h2.textContent.trim().toLowerCase();
      if (text.includes('you may also like') || text.includes('recently viewed')) {
        var section = h2.closest('.shopify-section') || h2.closest('section');
        if (section) section.style.display = showPostATC ? 'none' : '';
      }
    });
    // Also hide the product-recommendations section by ID
    var recsSection = document.querySelector('.shopify-section-product-recommendations');
    if (recsSection) recsSection.style.display = showPostATC ? 'none' : '';

    // Show/hide post-ATC
    var postAtc = document.getElementById('bundle-post-atc');
    if (postAtc) {
      postAtc.style.display = showPostATC ? 'block' : 'none';
    }
  }

  // --- Post-ATC nudge text override ---
  // After ATC, replace the tier-based nudge with discovery messaging
  // in the post-ATC stepper only. Uses MutationObserver with re-entry
  // guard to keep the override even if the stepper re-renders.
  var patcNudgeGuard = false;
  function overridePostAtcNudge() {
    var postAtc = document.getElementById('bundle-post-atc');
    if (!postAtc) return;
    var nudge = postAtc.querySelector('[data-nudge-text]');
    if (!nudge) return;
    patcNudgeGuard = true;
    nudge.textContent = 'Other items you may love';
    nudge.style.color = 'var(--tht-coral-deep, #E33B4D)';
    patcNudgeGuard = false;

    // Watch for the stepper's render() resetting the text
    if (!nudge._patcObserver) {
      var obs = new MutationObserver(function() {
        if (patcNudgeGuard) return;
        if (nudge.textContent !== 'Other items you may love') {
          patcNudgeGuard = true;
          nudge.textContent = 'Other items you may love';
          nudge.style.color = 'var(--tht-coral-deep, #E33B4D)';
          patcNudgeGuard = false;
        }
      });
      obs.observe(nudge, { childList: true, characterData: true, subtree: true });
      nudge._patcObserver = obs;
    }
  }

  // --- Initial state: hide post-ATC section on page load ---
  function initSections() {
    var postAtc = document.getElementById('bundle-post-atc');
    if (postAtc) postAtc.style.display = 'none';
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSections);
  } else {
    initSections();
  }

  // --- CSS: suppress the btn-fill sliding animation, keep text visible ---
  var s = document.createElement('style');
  s.textContent =
    '.product-form__submit[aria-busy="true"] .btn-fill,' +
    '.product-form__submit[aria-busy="true"]::after,' +
    '.product-form__submit[aria-busy="true"]::before{' +
    'display:none!important;opacity:0!important}' +
    '.product-form__submit[aria-busy="true"]{opacity:.7;cursor:wait}' +
    '.product-form__submit[aria-busy="true"] .btn-text{' +
    'opacity:1!important;visibility:visible!important}';
  document.head.appendChild(s);

  // ============================================================================
  // S30: B3G1F count tracker, PDP CTA at count=3, confetti listener
  // ============================================================================

  var lastBundleCount = null;

  // Polls until the preferred confetti target is in viewport, then fires the callback.
  // Falls back to a 1500ms hard cap so we never hang. The "preferred target" is the
  // inner .bt of #bundle-post-atc when it's visible (display:block), else any .bt
  // outside post-atc — same selection logic as maybeFireConfettiNow.
  //
  // History: an earlier version polled scrollY stability but produced false-positives
  // when the smooth scroll was queued via requestAnimationFrame and hadn't begun moving
  // pageYOffset yet — the polling saw 100ms of stillness and exited prematurely, before
  // the post-ATC card had reached the viewport.
  function afterScrollSettle(cb) {
    var startedAt = Date.now();
    function pickTarget() {
      var postAtc = document.getElementById('bundle-post-atc');
      var postAtcVisible = postAtc && postAtc.offsetParent !== null;
      if (postAtcVisible) {
        var inner = postAtc.querySelector('.bt');
        return inner || postAtc;
      }
      var anyBt = document.querySelectorAll('.bt');
      for (var i = 0; i < anyBt.length; i++) {
        if (!postAtc || !postAtc.contains(anyBt[i])) return anyBt[i];
      }
      return null;
    }
    function check() {
      if (Date.now() - startedAt > 1500) { cb(); return; }
      var target = pickTarget();
      if (target && isVisibleEnough(target)) { cb(); return; }
      requestAnimationFrame(check);
    }
    requestAnimationFrame(check);
  }

  function computeBundleCount(cart) {
    if (!cart || !cart.items) return 0;
    var c = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.gift_card) continue;
      var pt = (it.product_type || '').toLowerCase().trim(), ex = false;
      for (var j = 0; j < EXCLUDED_TYPES.length; j++) {
        if (pt === EXCLUDED_TYPES[j]) { ex = true; break; }
      }
      if (ex) continue;
      c += it.quantity;
    }
    return c;
  }

  // PDP CTA at count=3 — coral takeover + "Add free shirt to cart" label.
  // Skips the label change when the button is showing a transient state
  // (aria-busy=true → "Added!" / "Error"), but still toggles the .atc--unlock class
  // so the background colour reflects state through the busy window.
  function updatePdpAtcAtCount3(count) {
    var btns = document.querySelectorAll('.product-form__submit');
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      var textSpan = btn.querySelector('.btn-text > span');
      if (!textSpan) continue;
      // Lazily capture the original label the first time we touch this button.
      if (!btn.dataset.thtOriginalLabel) {
        btn.dataset.thtOriginalLabel = textSpan.textContent.trim();
      }
      var busy = btn.getAttribute('aria-busy') === 'true';
      if (count === 3) {
        btn.classList.add('atc--unlock');
        if (!busy && textSpan.textContent !== 'Add free shirt to cart') {
          textSpan.textContent = 'Add free shirt to cart';
        }
      } else {
        btn.classList.remove('atc--unlock');
        if (!busy && textSpan.textContent === 'Add free shirt to cart') {
          textSpan.textContent = btn.dataset.thtOriginalLabel;
        }
      }
    }
  }

  // Track cart count, dispatch unlock event on 2→3 transition (and on first-load
  // ready state when the session flag is unset). Called on every cart event.
  function trackBundleCount() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var count = computeBundleCount(cart);
        var prev = lastBundleCount;
        lastBundleCount = count;

        updatePdpAtcAtCount3(count);

        // Unlock dispatch on 2→3 transition. Initial-load case: prev is null — dispatch
        // only if confetti hasn't fired this session and we're at 3 (not 4+). Counts >= 4
        // don't dispatch (the user has already moved past the unlock moment).
        // The dispatch sets a *pending* flag in sessionStorage. The actual confetti burst
        // fires only when a punch card surface (PDP .bt or #bundle-post-atc) is in view —
        // see ensureConfettiObserver / maybeFireConfettiNow below.
        var crossedToThree = (prev !== null && prev < 3 && count >= 3);
        var initialAtThree = (prev === null && count === 3 && !sessionStorage.getItem('tht_b3g1_confetti_fired'));
        if (crossedToThree || initialAtThree) {
          if (!sessionStorage.getItem('tht_b3g1_confetti_fired')) {
            sessionStorage.setItem('tht_b3g1_confetti_pending', '1');
          }
          // Analytics-friendly dispatch — listeners can hook here without owning confetti.
          document.dispatchEvent(new CustomEvent('punch-card:unlocked', {
            detail: { count: count, source: 'bundle-cart-behavior' }
          }));
          // Defer until scroll settles — the ATC handler queues a smooth scroll
          // to post-ATC, and firing during that animation captures stale offscreen
          // coords (the PDP .bt is mid-flight out of viewport at +50ms). Once the
          // scroll lands, post-ATC's inner .bt is in view and that's the right origin.
          afterScrollSettle(function() {
            ensureConfettiObserver();
            maybeFireConfettiNow();
          });
        }
      })
      .catch(function() {});
  }

  // ---------- Confetti gating: pending flag + IntersectionObserver ----------
  // Flow:
  //   1. trackBundleCount detects 2→3 → sets sessionStorage 'tht_b3g1_confetti_pending'
  //      + dispatches `punch-card:unlocked` (analytics).
  //   2. maybeFireConfettiNow checks immediately for currently-visible targets.
  //   3. confettiObserver fires when a target scrolls into view later
  //      (post-ATC reveal, scroll-to, navigation to PDP).
  //   4. fireConfetti sets 'tht_b3g1_confetti_fired' and clears pending. Once per session.

  function findGiftRect(target) {
    var gift = target.querySelector && target.querySelector('.bt-step--reward .bt-circle');
    if (gift && gift.offsetWidth) return gift.getBoundingClientRect();
    return target.getBoundingClientRect();
  }

  function isVisibleEnough(el) {
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    if (r.bottom < 0 || r.top > window.innerHeight) return false;
    var visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    return (visible / r.height) >= 0.3;
  }

  function maybeFireConfettiNow() {
    if (sessionStorage.getItem('tht_b3g1_confetti_fired')) return;
    if (!sessionStorage.getItem('tht_b3g1_confetti_pending')) return;

    // Prefer post-ATC's inner .bt when post-ATC is visible. Falls back to PDP .bt
    // only when post-ATC isn't on the page or isn't shown (e.g. non-product pages,
    // or initial-load count=3 case before ATC).
    var postAtc = document.getElementById('bundle-post-atc');
    var postAtcVisible = postAtc && postAtc.offsetParent !== null && isVisibleEnough(postAtc);

    var targets;
    if (postAtcVisible) {
      var innerBt = postAtc.querySelector('.bt');
      targets = innerBt ? [innerBt] : [postAtc];
    } else {
      // Post-ATC not yet visible — try any visible .bt outside post-ATC.
      targets = Array.prototype.slice.call(document.querySelectorAll('.bt'))
        .filter(function(el) { return !postAtc || !postAtc.contains(el); });
    }

    for (var i = 0; i < targets.length; i++) {
      if (!isVisibleEnough(targets[i])) continue;
      fireConfetti(findGiftRect(targets[i]));
      return;
    }
  }

  var confettiObserver = null;
  function ensureConfettiObserver() {
    if (sessionStorage.getItem('tht_b3g1_confetti_fired')) {
      if (confettiObserver) { confettiObserver.disconnect(); confettiObserver = null; }
      return;
    }
    if (typeof IntersectionObserver === 'undefined') return;

    // Same target-preference logic as maybeFireConfettiNow — observe post-ATC's
    // inner .bt when post-ATC is visible (display:block + on-page), else the
    // PDP-level .bt as a fallback.
    var postAtc = document.getElementById('bundle-post-atc');
    var postAtcVisible = postAtc && postAtc.offsetParent !== null;
    var targets;
    if (postAtcVisible) {
      var innerBt = postAtc.querySelector('.bt');
      targets = innerBt ? [innerBt] : [postAtc];
    } else {
      targets = Array.prototype.slice.call(document.querySelectorAll('.bt'))
        .filter(function(el) { return !postAtc || !postAtc.contains(el); });
    }
    if (!targets.length) return;

    if (!confettiObserver) {
      confettiObserver = new IntersectionObserver(function(entries) {
        if (sessionStorage.getItem('tht_b3g1_confetti_fired')) {
          confettiObserver.disconnect(); confettiObserver = null; return;
        }
        if (!sessionStorage.getItem('tht_b3g1_confetti_pending')) return;
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          fireConfetti(findGiftRect(entries[i].target));
          return; // fireConfetti disconnects below
        }
      }, { threshold: 0.3 });
    }
    targets.forEach(function(t) { confettiObserver.observe(t); });
  }

  // ---------- Confetti burst (canvas-confetti, library-driven) ----------
  function fireConfetti(originRect) {
    if (sessionStorage.getItem('tht_b3g1_confetti_fired')) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sessionStorage.setItem('tht_b3g1_confetti_fired', '1');
      sessionStorage.removeItem('tht_b3g1_confetti_pending');
      if (confettiObserver) { confettiObserver.disconnect(); confettiObserver = null; }
      return;
    }

    // Library not loaded yet (slow connection, defer hadn't fired). Bail silently;
    // the IO observer will retry on next intersection. Don't set fired flag.
    if (typeof window.confetti !== 'function') return;

    // Translate originRect (viewport pixel coords) to canvas-confetti's normalized
    // origin, which is fractional viewport coordinates (0–1 on each axis).
    var ox, oy;
    if (originRect && originRect.width) {
      ox = (originRect.left + originRect.width / 2) / window.innerWidth;
      oy = (originRect.top + originRect.height / 2) / window.innerHeight;
    } else {
      ox = 0.5;
      oy = 0.4;
    }
    // Clamp — canvas-confetti tolerates out-of-range but visuals get weird.
    ox = Math.max(0, Math.min(1, ox));
    oy = Math.max(0, Math.min(1, oy));

    var colors = ['#00A3E0', '#FC5666', '#ffffff', '#0089BD', '#E33B4D'];
    var isMobile = window.innerWidth < 768;

    // Two-burst pattern. First burst: dense upward spread from the gift node.
    // Second burst (60ms later): wider angle, lighter density — the "second wave"
    // effect that makes celebrations feel produced.
    window.confetti({
      particleCount: isMobile ? 50 : 80,
      spread: 70,
      startVelocity: 45,
      origin: { x: ox, y: oy },
      colors: colors,
      shapes: ['square', 'circle'],
      scalar: 1.0,
      ticks: 200,
      gravity: 1.1,
      drift: 0
    });
    setTimeout(function() {
      if (typeof window.confetti !== 'function') return;
      window.confetti({
        particleCount: isMobile ? 25 : 40,
        spread: 100,
        startVelocity: 35,
        origin: { x: ox, y: oy },
        colors: colors,
        shapes: ['square', 'circle'],
        scalar: 0.8,
        ticks: 180,
        gravity: 1.2,
        drift: (Math.random() - 0.5) * 0.4
      });
    }, 60);

    sessionStorage.setItem('tht_b3g1_confetti_fired', '1');
    sessionStorage.removeItem('tht_b3g1_confetti_pending');
    if (confettiObserver) { confettiObserver.disconnect(); confettiObserver = null; }
  }

  // Drawer ready-state pill CTA — close drawer, scroll to post-ATC if present, else navigate.
  document.addEventListener('click', function(e) {
    var trigger = e.target.closest && e.target.closest('[data-tht-unlock-cta]');
    if (!trigger) return;
    e.preventDefault();
    var drawer = document.querySelector('cart-drawer');
    var closeBtn = drawer && drawer.querySelector('.drawer__close');
    if (closeBtn) closeBtn.click();
    setTimeout(function() {
      var postAtc = document.getElementById('bundle-post-atc');
      if (postAtc) {
        var header = document.querySelector('.shopify-section-header') || document.querySelector('header');
        var headerH = header ? header.getBoundingClientRect().height : 0;
        var targetY = postAtc.getBoundingClientRect().top + window.pageYOffset - headerH - 20;
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      } else {
        window.location.href = '/collections/all';
      }
    }, 280);
  });

  // ---------- Wire up cart events + initial pass ----------
  document.addEventListener('cart:updated', trackBundleCount);
  document.addEventListener('cart:change', trackBundleCount);
  document.addEventListener('bundle:cart-updated', trackBundleCount);
  document.addEventListener('ajaxProduct:added', function() { setTimeout(trackBundleCount, 500); });

  // Confetti observer setup — initial + after each cart event so we pick up
  // newly-rendered targets (e.g. post-ATC display:none → block, drawer reflow).
  function initBundle() {
    trackBundleCount();
    ensureConfettiObserver();
    // Cover the page-load case where the user lands at count >= 3 with pending set.
    setTimeout(maybeFireConfettiNow, 100);
  }
  document.addEventListener('cart:updated', ensureConfettiObserver);
  document.addEventListener('bundle:cart-updated', ensureConfettiObserver);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundle);
  } else {
    initBundle();
  }
})();